import { CategoryDialog } from '@/components/categories/category-dialog';
import { PrismEntityDialog } from '@/components/entities/prism-entity-dialog';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { FormDialog } from '@/components/ui/form-dialog';
import { Input } from '@/components/ui/input';
import { PageHeader } from "@/components/ui/page-header";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { useToast } from '@/components/ui/use-toast';
import { useCategoryQuery } from '@/hooks/useCategoryQueries';
import { useDialog } from '@/hooks/useDialog';
import { entityKeys, useCreateEntity, useDeleteEntity, useProjectEntities, useUpdateEntity } from '@/services/query/entities-queries';
import { getTemplates, Template } from '@/services/supabase/templates';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import {
    BookmarkIcon,
    Calendar,
    LayoutGrid,
    List as ListIcon,
    MapPinIcon,
    MoreHorizontal,
    Pencil,
    Plus,
    Search,
    Trash2,
    UserIcon
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Import the ExtendedEntity type
interface ExtendedEntity {
    id: number;
    name: string;
    description: string | null;
    status: string;
    template_id: number;
    created_at: string;
    updated_at: string | null;
    templates?: {
        name?: string;
        template_categories?: {
            name?: string;
        }
    }
    // FormDialog might add extra fields if not careful, but ExtendedEntity is from DB usually.
    category_id?: number;
}

export default function ElementsCategoryPage() {
    const { projectId, categoryId } = useParams();
    const navigate = useNavigate();
    const { toast } = useToast();
    const { openDialog, registerDialog } = useDialog();
    const createEntityMutation = useCreateEntity();
    const updateEntityMutation = useUpdateEntity();
    const deleteEntityMutation = useDeleteEntity();
    const queryClient = useQueryClient();

    // Dialog stats
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [editingEntity, setEditingEntity] = useState<ExtendedEntity | null>(null);

    // Category Dialog State
    const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
    const [categoryDialogMode, setCategoryDialogMode] = useState<'create' | 'edit'>('create');

    // View state
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [searchQuery, setSearchQuery] = useState('');

    // Register dialog components
    useEffect(() => {
        // Keeping these for now if other parts need them, but we are using custom dialog
        registerDialog('createElement', FormDialog);
        registerDialog('editEntity', FormDialog);

        // Import the ConfirmDeleteDialog dynamically to avoid circular dependencies
        import('@/components/dialogs/ConfirmDeleteDialog').then(module => {
            registerDialog('confirm-delete', module.ConfirmDeleteDialog);
        });
    }, [registerDialog]);

    // Fetch category details
    const { data: category, isLoading: categoryLoading } = useCategoryQuery(categoryId);

    // Fetch all entities for the project
    const { data: allEntities = [], isLoading: entitiesLoading } = useProjectEntities(projectId);

    // Filter entities by category_id and search query
    const categoryEntities = allEntities.filter(entity => {
        const matchesCategory = entity.category_id === parseInt(categoryId || '0');

        if (!matchesCategory) return false;

        if (searchQuery.trim() === '') return true;

        const query = searchQuery.toLowerCase();
        return (
            entity.name.toLowerCase().includes(query) ||
            (entity.description && entity.description.toLowerCase().includes(query)) ||
            (entity.status && entity.status.toLowerCase().includes(query))
        );
    });

    // State for templates
    const [templates, setTemplates] = useState<Template[]>([]);

    // Fetch all templates since they're no longer associated with categories
    useEffect(() => {
        let isMounted = true;

        if (projectId) {
            getTemplates(parseInt(projectId))
                .then(templatesData => {
                    if (isMounted) {
                        setTemplates(templatesData);
                    }
                })
                .catch(error => {
                    if (isMounted) {
                        console.error('Error fetching templates:', error);
                    }
                });
        }

        return () => {
            isMounted = false;
        };
    }, [projectId]);

    const handleSaveCreate = async (data: { name: string; description: string; template_id?: string }) => {
        try {
            if (!projectId || !categoryId) return;

            const newEntity = await createEntityMutation.mutateAsync({
                projectId: projectId,
                data: {
                    name: data.name,
                    description: data.description,
                    template_id: parseInt(data.template_id || '0'),
                    category_id: parseInt(categoryId),
                    status: 'active'
                }
            });

            queryClient.invalidateQueries({ queryKey: entityKeys.byProject(projectId) });
            toast({ title: "Entity Created", description: `${data.name} created successfully` });

            navigate(`/projects/${projectId}/entities/${newEntity.id}`);
        } catch (error) {
            console.error(error);
            toast({ title: "Error", description: "Failed to create entity", variant: "destructive" });
        }
    };

    const handleSaveEdit = async (data: { name: string; description: string; status?: string; template_id?: string }) => {
        try {
            if (!editingEntity) return;

            await updateEntityMutation.mutateAsync({
                entityId: editingEntity.id,
                data: {
                    name: data.name,
                    description: data.description || null,
                    status: data.status
                }
            });

            if (projectId) {
                queryClient.invalidateQueries({ queryKey: entityKeys.byProject(projectId) });
            }
            toast({ title: "Entity Updated", description: `${data.name} updated successfully` });
            setEditingEntity(null);
        } catch (error) {
            toast({ title: "Error", description: "Failed to update entity", variant: "destructive" });
        }
    };

    const handleEditEntity = (entity: ExtendedEntity, e: React.MouseEvent) => {
        e.stopPropagation();
        setEditingEntity(entity);
    };

    const handleDeleteEntity = (entity: ExtendedEntity, e: React.MouseEvent) => {
        e.stopPropagation();

        openDialog('confirm-delete', {
            itemType: 'entity',
            itemName: entity.name
        }, async (confirmed: boolean) => {
            if (!confirmed) return;

            try {
                await deleteEntityMutation.mutateAsync({
                    entityId: entity.id,
                    projectId: projectId
                });

                toast({ title: "Entity Deleted", description: `${entity.name} has been deleted successfully.` });

                if (projectId) {
                    queryClient.invalidateQueries({ queryKey: entityKeys.byProject(projectId) });
                }
            } catch (error) {
                toast({ title: "Error Deleting Entity", description: "Failed to delete entity", variant: "destructive" });
            }
        });
    };

    // Helper functions (same as before)
    const getCategoryIcon = (name: string | undefined) => {
        const lowercaseName = name?.toLowerCase() || '';

        if (lowercaseName.includes('character') || lowercaseName.includes('person')) {
            return <UserIcon className="h-5 w-5 md:h-6 md:w-6" />;
        } else if (lowercaseName.includes('location') || lowercaseName.includes('place')) {
            return <MapPinIcon className="h-5 w-5 md:h-6 md:w-6" />;
        } else {
            return <BookmarkIcon className="h-5 w-5 md:h-6 md:w-6" />;
        }
    };

    const handleEntityClick = (entityId: number | string) => {
        navigate(`/projects/${projectId}/entities/${entityId}`);
    };

    if (categoryLoading) {
        return (
            <div className="container max-w-7xl mx-auto py-8 px-6 flex items-center justify-center min-h-[50vh]">
                <div className="flex flex-col items-center gap-2">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
                    <p className="text-muted-foreground animate-pulse">Loading category...</p>
                </div>
            </div>
        );
    }

    if (!category) {
        return (
            <div className="container max-w-7xl mx-auto py-12 px-6 text-center">
                <h1 className="text-2xl font-bold mb-2">Category Not Found</h1>
                <p className="text-muted-foreground">The category you're looking for doesn't exist or couldn't be loaded.</p>
            </div>
        );
    }

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.05
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.3 }
        }
    };

    return (
        <div className="min-h-screen bg-transparent text-foreground flex flex-col font-sans">
            <main className="flex-1 w-full max-w-7xl mx-auto px-6 py-10">
                {/* Header Section */}
                <PageHeader
                    title={
                        <div className="flex items-center gap-3">
                            {/* Icon removed as requested */}
                            <span>{category.name}</span>
                        </div>
                    }
                    description={category.description || `Manage your ${category.name.toLowerCase()} for this project.`}
                    actions={
                        <div className="flex items-center gap-3">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setCategoryDialogMode('edit');
                                    setIsCategoryDialogOpen(true);
                                }}
                            >
                                <Pencil className="mr-2 h-4 w-4" />
                                Edit Category
                            </Button>



                            <Button onClick={() => setIsCreateDialogOpen(true)} className="shadow-lg shadow-primary/20">
                                <Plus className="mr-2 h-4 w-4" />
                                New {category.name.slice(0, -1) || 'Entity'}
                            </Button>
                        </div>
                    }
                >
                    {/* Controls Bar */}
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 w-full py-4 border-b border-border/40">
                        <div className="relative w-full sm:max-w-xs">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder={`Search ${category.name.toLowerCase()}...`}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9 bg-muted/30 border-border/50 focus:bg-background transition-colors"
                            />
                        </div>

                        <div className="flex items-center gap-2 bg-muted/30 p-1 rounded-lg border border-border/50 self-end sm:self-auto">
                            <Button
                                variant={viewMode === 'grid' ? "secondary" : "ghost"}
                                size="sm"
                                onClick={() => setViewMode('grid')}
                                className="h-8 px-2 lg:px-3"
                                title="Grid View"
                            >
                                <LayoutGrid className="h-4 w-4 mr-0 lg:mr-2" />
                                <span className="hidden lg:inline">Grid</span>
                            </Button>
                            <Button
                                variant={viewMode === 'list' ? "secondary" : "ghost"}
                                size="sm"
                                onClick={() => setViewMode('list')}
                                className="h-8 px-2 lg:px-3"
                                title="List View"
                            >
                                <ListIcon className="h-4 w-4 mr-0 lg:mr-2" />
                                <span className="hidden lg:inline">List</span>
                            </Button>
                        </div>
                    </div>
                </PageHeader>

                {/* Content Area */}
                <AnimatePresence mode="wait">
                    {entitiesLoading ? (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="text-center py-20 text-muted-foreground"
                        >
                            Loading entities...
                        </motion.div>
                    ) : categoryEntities.length === 0 ? (
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.3 }}
                            className="flex flex-col items-center justify-center py-20 px-4 text-center border-2 border-dashed border-border/50 rounded-xl bg-card/10"
                        >
                            <div className="h-16 w-16 rounded-full bg-muted/20 flex items-center justify-center mb-6">
                                {getCategoryIcon(category.name)}
                            </div>
                            <h3 className="text-xl font-semibold mb-2">No {category.name} Found</h3>
                            <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                                {searchQuery
                                    ? `No matches found for "${searchQuery}".`
                                    : `You haven't created any ${category.name.toLowerCase()} yet. Start building your world!`
                                }
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3">

                                <Button onClick={() => setIsCreateDialogOpen(true)} className="gap-2 shadow-lg shadow-primary/20">
                                    <Plus className="h-4 w-4" />
                                    Create Manually
                                </Button>
                            </div>
                        </motion.div>
                    ) : (
                        viewMode === 'grid' ? (
                            <motion.div
                                key="grid-view"
                                variants={containerVariants}
                                initial="hidden"
                                animate="visible"
                                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                            >
                                {categoryEntities.map((entity) => (
                                    <motion.div key={entity.id} variants={itemVariants} layout>
                                        <Card
                                            className="bg-card border-border shadow-sm hover:shadow-md hover:border-border transition-all duration-200 overflow-hidden flex flex-col h-full rounded-2xl group/card"
                                            onClick={() => handleEntityClick(entity.id)}
                                        >
                                            <div className="flex items-center justify-between p-6 border-b border-border bg-transparent transition-colors duration-200">
                                                <div className="flex items-center gap-2.5 min-w-0">
                                                    <h3 className="text-base font-semibold text-card-foreground tracking-tight truncate">
                                                        {entity.name}
                                                    </h3>
                                                    <Badge 
                                                        variant="secondary" 
                                                        className={cn(
                                                            "px-1.5 h-5 text-[10px] flex items-center justify-center border-border rounded-md shrink-0",
                                                            entity.status === 'active' ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" : "bg-muted text-muted-foreground"
                                                        )}
                                                    >
                                                        {entity.status || 'Active'}
                                                    </Badge>
                                                </div>
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-8 w-8 -mr-2 text-muted-foreground/50 hover:text-foreground transition-colors"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <MoreHorizontal className="h-4 w-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        <DropdownMenuItem onClick={(e) => handleEditEntity(entity, e)}>
                                                            <Pencil className="h-4 w-4 mr-2" /> Edit
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem
                                                            onClick={(e) => handleDeleteEntity(entity, e)}
                                                            className="text-destructive focus:text-destructive"
                                                        >
                                                            <Trash2 className="h-4 w-4 mr-2" /> Delete
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </div>

                                            <CardContent className="p-6 flex-1 relative bg-transparent flex flex-col gap-3">
                                                <div className="flex items-center gap-2 text-xs text-muted-foreground font-medium">
                                                    <Badge variant="outline" className="rounded-sm font-normal text-[10px] px-1.5 h-5">
                                                        {(entity as ExtendedEntity).templates?.name || 'Template'}
                                                    </Badge>
                                                    <span className="flex items-center gap-1 opacity-70">
                                                        <Calendar className="h-3 w-3" />
                                                        {format(new Date(entity.created_at), 'MMM d')}
                                                    </span>
                                                </div>
                                                
                                                <p className="text-sm text-muted-foreground/80 line-clamp-3 leading-relaxed">
                                                    {entity.description || 'No description provided.'}
                                                </p>
                                            </CardContent>
                                        </Card>
                                    </motion.div>
                                ))}
                            </motion.div>
                        ) : (
                            <motion.div
                                key="list-view"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.2 }}
                                className="rounded-md border border-border bg-card/50 backdrop-blur-sm shadow-sm overflow-hidden"
                            >
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-muted/30 hover:bg-muted/30">
                                            <TableHead className="w-[30%]">Name</TableHead>
                                            <TableHead className="w-[30%]">Template</TableHead>
                                            <TableHead className="w-[20%]">Status</TableHead>
                                            <TableHead className="w-[15%]">Created</TableHead>
                                            <TableHead className="w-[5%] text-right"></TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {categoryEntities.map((entity) => (
                                            <TableRow
                                                key={entity.id}
                                                className="cursor-pointer hover:bg-muted/50 transition-colors"
                                                onClick={() => handleEntityClick(entity.id)}
                                            >
                                                <TableCell className="font-medium">
                                                    <div className="flex flex-col">
                                                        <span className="text-base font-semibold">{entity.name}</span>
                                                        <span className="text-xs text-muted-foreground line-clamp-1 truncate max-w-[200px]">
                                                            {entity.description}
                                                        </span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-muted-foreground">
                                                    {(entity as ExtendedEntity).templates?.name || '-'}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant="secondary" className="capitalize text-xs font-normal">
                                                        {entity.status || 'Active'}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="text-sm text-muted-foreground font-mono">
                                                    {format(new Date(entity.created_at), 'MMM d, yyyy')}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-8 w-8"
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                <MoreHorizontal className="h-4 w-4 text-muted-foreground" />
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end">
                                                            <DropdownMenuItem onClick={(e) => handleEditEntity(entity, e)}>
                                                                <Pencil className="h-4 w-4 mr-2" /> Edit
                                                            </DropdownMenuItem>
                                                            <DropdownMenuItem
                                                                onClick={(e) => handleDeleteEntity(entity, e)}
                                                                className="text-destructive focus:text-destructive"
                                                            >
                                                                <Trash2 className="h-4 w-4 mr-2" /> Delete
                                                            </DropdownMenuItem>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </motion.div>
                        )
                    )}
                </AnimatePresence>

                {/* AI Entity Creation Dialog */}


                {/* Unified Entity Creation/Edit Dialog - Experimental Prism Design */}
                <PrismEntityDialog
                    isOpen={isCreateDialogOpen || !!editingEntity}
                    onClose={() => {
                        setIsCreateDialogOpen(false);
                        setEditingEntity(null);
                    }}
                    onSave={editingEntity ? handleSaveEdit : handleSaveCreate}
                    entity={editingEntity || undefined}
                    templates={templates}
                    categoryName={category.name}
                />

                {/* Category Creation/Edit Dialog */}
                <CategoryDialog
                    isOpen={isCategoryDialogOpen}
                    onClose={() => setIsCategoryDialogOpen(false)}
                    mode={categoryDialogMode}
                    category={category}
                    projectId={parseInt(projectId || '0')}
                />
            </main>
        </div>
    );
}

// Check if I missed any curly braces. 
// Function ends at line 764.
// Looks matching.
